import React, { MouseEventHandler, useState } from 'react'
import styled, { css } from 'styled-components'

import { useAttributes } from '@hozana/dom/hooks/useAttributes'
import { QA } from '@hozana/tests/constants'

import { RoundButton } from 'elements/button/RoundButton'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Row, TRowProps } from 'elements/layout/Row'
import { StretchCell } from 'elements/layout/StretchCell'
import { Ellipsis } from 'elements/text/Ellipsis'
import { P } from 'elements/text/P'
import { Text } from 'elements/text/Text'

import { ICON } from 'config/icons'

import { useMyIntention } from 'modules/intention/hooks/useMyIntention'

import { IntentionPrayersListButton } from './IntentionPrayersListButton'
import { PublishedUntil } from './PublishedUntil'

type TStyledRowProps = { toggle?: boolean } & TRowProps

const StyledRow = styled(Row)<TStyledRowProps>`
  ${(props) =>
    props.toggle &&
    css`
      transform: translate(-50%, 0);
    `};
`

export type TCurrentIntentionProps = {
  handleClearIntention: MouseEventHandler<HTMLButtonElement>
  justPublished?: boolean
  toggleEditIntention: MouseEventHandler<HTMLButtonElement>
}

export const CurrentIntention: TFC<TCurrentIntentionProps> = ({
  handleClearIntention,
  justPublished,
  toggleEditIntention,
}) => {
  const attrs = useAttributes()
  const myIntention = useMyIntention()
  const [optionsDisplayed, setOptionsDisplayed] = useState(false)

  return (
    <Div noOverflow>
      <Row m="0 0 5px 0">
        <Cell flex="1">
          <P
            fontSize="1em"
            p={myIntention.guestName ? '5px 0' : '5px 10px'}
            lineHeight="1.4em"
            data-testid={QA.INTENTION.YOUR_INTENTION}
          >
            {myIntention.guestName && (
              <>
                <Ellipsis color="black50" italic fontSize="0.9em">
                  trans:intention:words.your-intention
                </Ellipsis>
                <br />
                <Ellipsis bold>
                  {myIntention.guestName}
                  &nbsp;:&nbsp;
                </Ellipsis>
              </>
            )}
            <Text inline color={myIntention.guestName ? undefined : 'black'}>
              {myIntention.content}
            </Text>
          </P>
        </Cell>
      </Row>
      <StyledRow align="middleCenter" w="200%" gutter="5px" toggle={optionsDisplayed} transition>
        {/* We reverse the position of buttons in the DOM (with "order"), to improve the accessibility */}
        <Cell w="50%" align="middleBetween">
          <Row align="middleBetween" w="100%" gutter="7px">
            <Cell align="middleLeft">
              <PublishedUntil date={myIntention.publishedUntil} animate={justPublished} />
            </Cell>
            <StretchCell flex="1" align="middleLeft">
              <IntentionPrayersListButton />
            </StretchCell>
            <Cell>
              <RoundButton
                icon={ICON.ELLIPSIS_VERTICAL}
                color="black50"
                hoverColor="darkBlue"
                hoverBg="black10"
                onClick={() => setOptionsDisplayed(true)}
                data-testid={QA.INTENTION.OPTIONS}
                label="trans:intention:word.options"
                ariaLabel="trans:intention:intention.options"
                {...attrs}
              />
            </Cell>
          </Row>
        </Cell>
        <Cell w="50%">
          <Row w="100%" gutter="7px">
            <Cell flex="1">
              <RoundButton
                icon={ICON.PENCIL}
                color="black50"
                hoverColor="darkBlue"
                hoverBg="black10"
                onClick={toggleEditIntention}
                data-testid={QA.INTENTION.EDIT}
                label="trans:intention:word.edit"
                ariaLabel="trans:intention:intention.edit"
                {...attrs}
              />
              <RoundButton
                icon={ICON.TRASH}
                color="black50"
                hoverColor="red"
                hoverBg="black10"
                onClick={handleClearIntention}
                data-testid={QA.INTENTION.REMOVE}
                label="trans:intention:word.remove"
                ariaLabel="trans:intention:intention.delete.delete"
                {...attrs}
              />
            </Cell>
            <Cell>
              <RoundButton
                icon={ICON.CLOSE}
                color="black50"
                hoverColor="darkBlue"
                hoverBg="black10"
                onClick={() => setOptionsDisplayed(false)}
                data-testid={QA.INTENTION.EDIT}
                label="trans:intention:word.close"
                ariaLabel="trans:intention:intention.edit"
                {...attrs}
              />
            </Cell>
          </Row>
        </Cell>
      </StyledRow>
    </Div>
  )
}
